// The Web Push on/off state machine for THIS browser, lifted out of
// `components/PushToggle.tsx` so two renderers share one: the topbar's bell
// button and the Notifications page's "Turn on for this device" switch (#5703).
//
// Two pure verdicts and a thin shell. `lib/push.ts` still owns the browser work
// (`enablePush` / `disablePush` over an injected `PushRuntime`); this file only
// decides which state a result lands on and holds the two signals.
//
// `subscribed` is the last CONFIRMED answer. A control paints it while
// `working`, so it never shows a click's guess: enabling can sit on the
// browser's permission prompt for as long as the person takes.

import { type Accessor, createSignal } from 'solid-js';
import {
  disablePush,
  type EnableResult,
  enablePush,
  errorSummary,
  PushEnableError,
  type PushEnableFailure,
  type PushRuntime,
} from './push';

/**
 * The i18n key of the sentence each enable failure shows (#5884). One record,
 * read by both renderers (the settings page and the topbar bell), so a new
 * reason cannot be named in one and missing in the other.
 */
export const PUSH_FAILURE_KEY: Readonly<Record<PushEnableFailure, string>> = {
  service_worker_unavailable: 'notifications_settings.browser.fail_service_worker',
  key_unavailable: 'notifications_settings.browser.fail_key',
  subscribe_refused: 'notifications_settings.browser.fail_subscribe',
  register_failed: 'notifications_settings.browser.fail_register',
};

export const PUSH_TOGGLE_STATUSES = [
  'loading',
  'unsupported',
  'blocked',
  'off',
  'on',
  'working',
] as const;
export type PushToggleStatus = (typeof PUSH_TOGGLE_STATUSES)[number];

/** A status the machine can come to rest on. */
type Settled = Exclude<PushToggleStatus, 'loading' | 'working'>;

/** What the browser says before anything is asked of it. Pure. */
export function initialPushStatus(probe: {
  readonly supported: boolean;
  readonly permission: NotificationPermission;
  readonly endpoint: string | null;
}): Settled {
  if (!probe.supported) return 'unsupported';
  if (probe.permission === 'denied') return 'blocked';
  return probe.endpoint === null ? 'off' : 'on';
}

/** Where an enable attempt lands. `error` is any step of the ladder throwing. Pure. */
export function statusAfterEnable(result: EnableResult | 'error'): Settled {
  switch (result) {
    case 'subscribed':
      return 'on';
    case 'denied':
      return 'blocked';
    case 'unsupported':
      return 'unsupported';
    case 'error':
      return 'off';
  }
}

export type PushToggle = {
  readonly status: Accessor<PushToggleStatus>;
  /** Is this browser subscribed, as last confirmed. */
  readonly subscribed: Accessor<boolean>;
  /**
   * The named reason the most recent enable attempt failed (#5884), or `null`
   * when no failure is showing. Cleared on `init`/`enable`/`disable`; the UI
   * reads it to render a sentence under the Notifications card and append it
   * to the bell button's title/aria-label.
   */
  readonly failure: Accessor<PushEnableFailure | null>;
  /** Reflect the browser's current state. Never calls `onChange`. */
  readonly init: () => Promise<void>;
  /** Subscribe this browser. A no-op unless the machine is `off`. */
  readonly enable: () => Promise<void>;
  /** Unsubscribe this browser. A no-op unless the machine is `on`. */
  readonly disable: () => Promise<void>;
};

/**
 * `onChange` fires after a subscribe or an unsubscribe LANDS, so a mounted
 * device list can re-read; never on the initial reflect and never on a failure.
 */
export function createPushToggle(rt: PushRuntime, onChange?: () => void): PushToggle {
  const [status, setStatus] = createSignal<PushToggleStatus>('loading');
  const [subscribed, setSubscribed] = createSignal(false);
  const [failure, setFailure] = createSignal<PushEnableFailure | null>(null);

  const settle = (next: Settled): void => {
    setSubscribed(next === 'on');
    setStatus(next);
  };

  async function init(): Promise<void> {
    setFailure(null);
    const supported = rt.supported();
    const permission = supported ? rt.permission() : 'default';
    // Only a browser that could be subscribed is asked whether it is: the probe
    // waits on the service worker, and an unsupported or blocked one has
    // nothing to say.
    const endpoint =
      supported && permission !== 'denied' ? await rt.currentEndpoint().catch(() => null) : null;
    settle(initialPushStatus({ supported, permission, endpoint }));
  }

  async function enable(): Promise<void> {
    if (status() !== 'off') return;
    setFailure(null);
    setStatus('working');
    let result: EnableResult;
    try {
      result = await enablePush(rt);
    } catch (e: unknown) {
      // Name the reason: a typed `PushEnableError` carries its own; anything
      // else (the permission step, which is not wrapped) is reported as
      // `register_failed`, and the console line below carries the real cause.
      const reason: PushEnableFailure = e instanceof PushEnableError ? e.reason : 'register_failed';
      // Dev console only. NEVER log subscription or key material — those are
      // the very things the API endpoint authenticates against.
      // Log the browser's own error (the wrapper's message only repeats the reason).
      const original = e instanceof PushEnableError ? e.cause : e;
      const summary = errorSummary(original);
      console.warn(`[dashboard] push: enable failed (${reason})`, summary);
      setFailure(reason);
      settle('off');
      return;
    }
    settle(statusAfterEnable(result));
    if (result === 'subscribed') onChange?.();
  }

  async function disable(): Promise<void> {
    // Clear the failure first, unconditionally — even the early-return path
    // for an already-off browser must NOT leave a stale failure sentence on
    // the page. A `failure` survives a no-op disable would be a lie.
    setFailure(null);
    if (status() !== 'on') return;
    setStatus('working');
    // Only report `off` once the cleanup succeeded: a swallowed failure would
    // show push as off while notifications keep arriving.
    try {
      await disablePush(rt);
    } catch {
      settle('on');
      return;
    }
    settle('off');
    onChange?.();
  }

  return { status, subscribed, failure, init, enable, disable };
}
